import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TextField,
  MenuItem,
  InputAdornment,
  IconButton,
  Chip,
  CircularProgress,
  Tooltip,
  Pagination,
  Badge,
  Alert,
  Snackbar,
  Stack
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import Breadcrumbs from '../../components/layout/Breadcrumbs';
import { 
  getProductVariants, 
  createProductVariant, 
  updateProductVariant, 
  searchProductVariants,
  uploadProductsFile,
  downloadProductTemplate
} from '../../services/productapi';
import VariantFormModal from '../../components/products/VariantFormModal';
import MarketplaceListingsModal from '../../components/products/MarketplaceListingsModal';
import FileUploadModalNew from '../../components/common/FileUploadModalNew';
import DataExportButton from '../../components/common/DataExportButton';

const VariantsPage = () => {  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [modalOpen, setModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [currentVariant, setCurrentVariant] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'  });
    // Pagination state
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(20); // Increased from 10 to 20 items per page
  const [totalCount, setTotalCount] = useState(0);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchTimeoutId, setSearchTimeoutId] = useState(null);
  
  // Marketplace listings modal state
  const [listingsModalOpen, setListingsModalOpen] = useState(false);
  const [selectedSku, setSelectedSku] = useState(null);
  
  // Sorting state
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const fetchDataWithStatus = async (pageNumber = null, status = null, isInitialLoad = false) => {
    // If there's already a fetch in progress and this is not the initial load, don't start another one
    if (loading && !isInitialLoad) {
      console.log('Fetch already in progress, skipping duplicate request');
      return;
    }
    
    try {
      setLoading(true);
      setIsSearchMode(false);
      setError(null); // Clear any previous errors
      
      // Use provided page number or current state
      const currentPage = pageNumber || page;
      // Use provided status or current statusFilter state
      const currentStatus = status !== null ? status : statusFilter;
      
      console.log(`Fetching variants data for page ${currentPage}, status filter: ${currentStatus}`);
        // Prepare params including status filter
      const params = {
        page: currentPage,
        page_size: rowsPerPage, // Always include page_size parameter
      };
      
      // Add state filter if not 'all'
      if (currentStatus !== 'all') {
        params.state = currentStatus;
      }
      
      // Add ordering if set
      if (sortField) {
        params.ordering = sortDirection === 'desc' ? `-${sortField}` : sortField;
      }
      
      // Add a cache buster to prevent browser/axios caching
      params._t = new Date().getTime();
      
      console.log('Sending API request with params:', params);      const response = await getProductVariants(params);
      
      // Handle paginated response
      if (response.data.results) {
        console.log(`Received ${response.data.results.length} variants, total: ${response.data.count}`);
        setVariants(response.data.results);
        setTotalCount(response.data.count);
      } else {
        // Fallback in case API doesn't return paginated response
        console.log(`Received ${response.data.length} variants (non-paginated)`);
        setVariants(response.data);
        setTotalCount(response.data.length);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching product variants:', err);
      setError('Failed to load variants data');
      setVariants([]); // Clear variants to avoid showing stale data
      setTotalCount(0);
      setLoading(false);
    }
  };
    const fetchData = async (pageNumber = null, isInitialLoad = false) => {
    // Always pass the current statusFilter to ensure server-side filtering is consistent
    return fetchDataWithStatus(pageNumber, statusFilter, isInitialLoad);
  };
  
  useEffect(() => {
    // Initial data load with setTimeout to ensure component is fully mounted
    // This helps prevent duplicate fetches on initial load
    const initializationTimeout = setTimeout(() => {
      console.log('Running initial variants data fetch');
      fetchData(null, true); // Pass true to indicate this is the initial load
    }, 0);
    
    // Cleanup function to clear timeouts when component unmounts
    return () => {
      clearTimeout(initializationTimeout);
      if (searchTimeoutId) {
        clearTimeout(searchTimeoutId);
      }
    };
    
    // Don't add page as dependency to avoid refetching on every page change
    // Page changes are handled in handleChangePage
  }, []);  const handleRefresh = () => {
    // Reset states before fetching fresh data
    setError(null);
    setVariants([]); // Clear variants to avoid showing stale data
    setTotalCount(0);
    setIsSearchMode(false); // Reset search mode
    setSearchTerm(''); // Clear search term
    setSortField(null); // Reset sorting
    setSortDirection('asc'); // Reset sort direction
      // Fetch fresh data (passing page 1 to ensure we start from the first page)
    fetchData(1);
  };
  
  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    
    // Clear any existing timeout to prevent multiple API calls
    if (searchTimeoutId) {
      clearTimeout(searchTimeoutId);
    }
    
    // Always reset to first page when searching
    const newPage = 1;
    setPage(newPage);
    
    // If the search term is empty, revert to regular data fetch immediately
    if (!value.trim()) {
      setSearchTimeoutId(null);
      fetchData(newPage);
      return;
    }
    
    // Debounce search to avoid too many API calls
    const timeoutId = setTimeout(() => {
      performSearch(value, newPage);
      setSearchTimeoutId(null); // Clear the timeout ID after execution
    }, 500);
      // Store the timeout ID so we can clear it if needed
    setSearchTimeoutId(timeoutId);
  };
  
  const performSearch = async (query, pageNumber = null) => {
    if (!query.trim()) return;
    
    // If there's already a fetch in progress, don't start another one
    if (loading) {
      console.log('Search already in progress, skipping duplicate request');
      return;
    }
    
    try {
      setLoading(true);
      setIsSearchMode(true);
      setError(null); // Clear any previous errors
      
      // Use provided page number or current state
      const currentPage = pageNumber || page;
        console.log(`Searching variants for "${query}" on page ${currentPage}`);
        // Add a cache buster parameter to prevent browser/axios caching
      const ordering = sortField ? (sortDirection === 'desc' ? `-${sortField}` : sortField) : null;
      const response = await searchProductVariants(
        query, 
        currentPage,  
        rowsPerPage,
        statusFilter, // status filter
        new Date().getTime(), // cache buster
        ordering // ordering
      );
      
      // Handle paginated search results
      if (response.data.results) {
        console.log(`Search returned ${response.data.results.length} variants, total: ${response.data.count}`);
        setVariants(response.data.results);
        setTotalCount(response.data.count);
      } else {
        // Fallback in case API doesn't return paginated response
        console.log(`Search returned ${response.data.length} variants (non-paginated)`);
        setVariants(response.data);
        setTotalCount(response.data.length);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error searching variants:', err);
      setError('Search failed. Please try again.');
      setVariants([]); // Clear variants to avoid showing stale data
      setTotalCount(0);
      setLoading(false);
    }
  };
  const performSearchWithStatus = async (query, pageNumber = null, statusFilterParam = null) => {
    if (!query.trim()) return;
    
    // If there's already a fetch in progress, don't start another one
    if (loading) {
      console.log('Search already in progress, skipping duplicate request');
      return;
    }
    
    try {
      setLoading(true);
      setIsSearchMode(true);
      setError(null); // Clear any previous errors
        // Use provided page number or current state
      const currentPage = pageNumber || page;
      // Use provided status filter or current statusFilter state  
      const currentStatus = statusFilterParam !== null ? statusFilterParam : statusFilter;
        console.log(`Searching variants for "${query}" on page ${currentPage} with status: ${currentStatus}`);
        // Add a cache buster parameter to prevent browser/axios caching
      const ordering = sortField ? (sortDirection === 'desc' ? `-${sortField}` : sortField) : null;
      const response = await searchProductVariants(
        query, 
        currentPage, 
        rowsPerPage,
        currentStatus, // status filter
        new Date().getTime(), // cache buster
        ordering // ordering
      );
      
      // Handle paginated search results
      if (response.data.results) {
        console.log(`Search returned ${response.data.results.length} variants, total: ${response.data.count}`);
        setVariants(response.data.results);
        setTotalCount(response.data.count);
      } else {
        // Fallback in case API doesn't return paginated response
        console.log(`Search returned ${response.data.length} variants (non-paginated)`);
        setVariants(response.data);
        setTotalCount(response.data.length);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error searching variants:', err);
      setError('Search failed. Please try again.');
      setVariants([]); // Clear variants to avoid showing stale data
      setTotalCount(0);      setLoading(false);
    }
  };

  const handleStatusChange = (event) => {
    const newStatusFilter = event.target.value;
    setStatusFilter(newStatusFilter);
    
    // Reset to first page when filtering
    const newPage = 1;
    setPage(newPage);
    
    // If in search mode and we have a search term, search with the new status filter
    if (isSearchMode && searchTerm) {
      // Use server-side filtering for search results too
      performSearchWithStatus(searchTerm, newPage, newStatusFilter);
    } else {
      // Otherwise, just fetch the new page of data with the new status filter
      // We need to create a custom call that uses the new status filter
      // since the state won't be updated yet      fetchDataWithStatus(newPage, newStatusFilter);
    }
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    
    // Log the page change for debugging
    console.log(`Changing to page ${newPage}`);
    
    // If we're in search mode, we need to search with the new page
    if (isSearchMode && searchTerm) {
      performSearch(searchTerm, newPage);
    } else {
      // Otherwise just fetch the new page of data
      fetchData(newPage);
    }
  };
    const handleAddVariant = () => {
    setCurrentVariant(null); // Reset current variant for adding a new one
    setModalOpen(true);
  };
  
  const handleOpenListingsModal = (variant) => {
    setSelectedSku(variant);
    setListingsModalOpen(true);
  };
  
  const handleCloseListingsModal = () => {
    setListingsModalOpen(false);
    setSelectedSku(null);
  };

  const handleEditVariant = (variant) => {
    setCurrentVariant(variant);
    setModalOpen(true);
  };
  
  const handleCloseModal = () => {
    setModalOpen(false);
    setCurrentVariant(null);
  };

  const handleOpenUploadModal = () => {
    setUploadModalOpen(true);
  };
  
  const handleCloseUploadModal = () => {
    setUploadModalOpen(false);
  };
  
  const handleUploadSuccess = () => {
    // Refresh the variants list after successful upload
    fetchData();
    setNotification({
      open: true,
      message: 'Variants were successfully uploaded!',
      severity: 'success'
    });
  };
  
  const handleSaveVariant = async (formData, mode) => {
    try {
      setSaveLoading(true);
      let response;
      
      if (mode === 'add') {
        // For new variants, FormData is already correctly formatted
        response = await createProductVariant(formData);
        setNotification({
          open: true,
          message: 'Variant created successfully!',
          severity: 'success'
        });
      } else {
        // For updates, ensure we're passing the correct ID and FormData object
        // No need to modify the FormData - it's already correctly formatted
        response = await updateProductVariant(currentVariant.id, formData);
        setNotification({
          open: true,
          message: 'Variant updated successfully!',
          severity: 'success'
        });
      }
      
      // Close modal and refresh data
      setModalOpen(false);
      setCurrentVariant(null);
      fetchData();
    } catch (error) {
      console.error('Error saving variant:', error);
      // Try to extract a more helpful error message from the API response
      let errorMessage = 'Failed to save variant';
      
      if (error.response && error.response.data) {
        // DRF often returns errors as an object with field names as keys
        if (typeof error.response.data === 'object') {
          const errorDetails = Object.entries(error.response.data)
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('; ');
          
          if (errorDetails) {
            errorMessage = errorDetails;
          } else if (error.response.data.detail) {
            errorMessage = error.response.data.detail;
          }
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setNotification({
        open: true,
        message: `Error: ${errorMessage}`,
        severity: 'error'
      });
    } finally {
      setSaveLoading(false);
    }
  };
  
  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };
  // No client-side filtering needed as we're using server-side filtering
  
  // Pagination calculations - using totalCount from API for server-side pagination
  const totalPages = Math.ceil(totalCount / rowsPerPage);
  
  // With server-side pagination and filtering, we don't need to modify the data,
  // as the API already returns the correct page of data with the correct filters
  const paginatedVariants = variants;
  
  // Helper function to get a color code based on color name
  const getColorCode = (colorName) => {
    if (!colorName) return '#9e9e9e';
    
    const colorMap = {
      'Red': '#f44336',
      'Green': '#4caf50',
      'Blue': '#2196f3',
      'Yellow': '#ffeb3b',
      'Orange': '#ff9800',
      'Purple': '#9c27b0',
      'Pink': '#e91e63',
      'Sea Green': '#009688',
      'Lime': '#cddc39',
      'Black': '#212121',
      'White': '#ffffff',
      'Grey': '#9e9e9e'
    };
    
    const key = Object.keys(colorMap).find(key => 
      colorName.toLowerCase().includes(key.toLowerCase())
    );
    
    return key ? colorMap[key] : '#9e9e9e'; // Default to grey if no match
  };
  
  const handleSort = (field) => {
    let newDirection = 'asc';
    
    // If clicking the same field, toggle direction
    if (sortField === field) {
      newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    }
    
    setSortField(field);
    setSortDirection(newDirection);
    
    // Reset to first page when sorting
    const newPage = 1;
    setPage(newPage);
    
    // Trigger data fetch with new sorting
    if (isSearchMode && searchTerm) {
      performSearchWithStatus(searchTerm, newPage, statusFilter);
    } else {
      fetchDataWithStatus(newPage, statusFilter);
    }
  };

  // Component for sortable table header
  const SortableTableCell = ({ field, children, ...props }) => {
    const isSorted = sortField === field;
    const isDesc = isSorted && sortDirection === 'desc';
    
    return (
      <TableCell
        {...props}
        sx={{
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': {
            backgroundColor: '#f0f0f0'
          },
          ...props.sx
        }}
        onClick={() => handleSort(field)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {children}
          {isSorted && (
            isDesc ? 
              <ArrowDownwardIcon fontSize="small" sx={{ color: '#1976d2' }} /> : 
              <ArrowUpwardIcon fontSize="small" sx={{ color: '#1976d2' }} />
          )}
        </Box>
      </TableCell>
    );
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Breadcrumbs items={[
        { label: 'Inventory Management', href: '/inventory' },
        { label: 'Variants' }
      ]} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 500, color: '#333' }}>
          Product Variants
        </Typography>
        
        <Stack direction="row" spacing={2}>
          <Button 
            variant="outlined" 
            startIcon={<CloudUploadIcon />}
            sx={{ textTransform: 'none' }}
            onClick={handleOpenUploadModal}
          >
            Upload Variants
          </Button>
          
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            sx={{ 
              bgcolor: '#3f51b5', 
              '&:hover': { bgcolor: '#303f9f' },
              textTransform: 'none'
            }}
            onClick={handleAddVariant}
          >
            Add New Variant
          </Button>
        </Stack>
      </Box>
      
      <Paper sx={{ mb: 3, p: 2, borderRadius: 2 }} elevation={0}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search variants..."
            size="small"
            value={searchTerm}
            onChange={handleSearch}
            sx={{ width: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={handleStatusChange}
            size="small"
            sx={{ width: 150 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
            <Tooltip title="Refresh variants data">
            <IconButton onClick={handleRefresh} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
            <DataExportButton 
            modelName="ProductVariant" 
            tooltipTitle="Export variants data"
          />
          
          <Tooltip title="Filter options">
            <IconButton>
              <FilterListIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={40} />
        </Box>
      ) : error ? (
        <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }} elevation={0}>
          <Typography color="error">{error}</Typography>
          <Button 
            startIcon={<RefreshIcon />} 
            onClick={handleRefresh}
            sx={{ mt: 2 }}
            variant="outlined"
          >
            Try Again
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Table sx={{ minWidth: 650 }}>            <TableHead>
              <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                <TableCell>SKU</TableCell>
                <TableCell>Product Name</TableCell>
                <TableCell>Color</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>UPC</TableCell>
                <SortableTableCell field="stock">Stock</SortableTableCell>
                <TableCell>Packed</TableCell>
                <SortableTableCell field="sale">Sales</SortableTableCell>
                <SortableTableCell field="return_quantity">Returns</SortableTableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedVariants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary">No variants found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedVariants.map((variant) => (
                  <TableRow key={variant.id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{variant.sku}</TableCell>
                    <TableCell>{variant.product_name || '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box 
                          sx={{ 
                            width: 14, 
                            height: 14, 
                            borderRadius: '50%', 
                            bgcolor: getColorCode(variant.color_name),
                            border: '1px solid #ddd'
                          }} 
                        />
                        {variant.color_name || '-'}
                      </Box>
                    </TableCell>
                    <TableCell>{variant.size_name || '-'}</TableCell>
                    <TableCell>{variant.upc || '-'}</TableCell>
                    <TableCell>
                      <Badge 
                        badgeContent={variant.stock < variant.reorder_level ? "Low" : null} 
                        color="error"
                        anchorOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                      >
                        <Box sx={{ minWidth: 30 }}>{variant.stock}</Box>
                      </Badge>
                    </TableCell>
                    <TableCell>{variant.stock_packed || 0}</TableCell>
                    <TableCell>{variant.sale || 0}</TableCell>
                    <TableCell>{variant.return_quantity || 0}</TableCell>
                    <TableCell>
                      <Chip
                        label={variant.state === 'active' ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{ 
                          bgcolor: variant.state === 'active' ? '#e3f2fd' : '#ffebee',
                          color: variant.state === 'active' ? '#1565c0' : '#c62828',
                          borderRadius: '16px',
                          fontSize: '0.75rem',
                          fontWeight: 500
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            color="info"
                            onClick={() => handleOpenListingsModal(variant)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Edit Variant">
                          <IconButton 
                            size="small" 
                            color="primary" 
                            onClick={() => handleEditVariant(variant)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}      {!loading && !error && variants.length > 0 && (        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
          <Pagination 
            count={totalPages} 
            page={page}onChange={(e, newPage) => {
              handleChangePage(e, newPage);
            }}
            color="primary" 
            size="medium" 
            showFirstButton
            showLastButton
          />
        </Box>
      )}
      
      {/* Variant Form Modal */}
      <VariantFormModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveVariant}
        variant={currentVariant}
        isLoading={saveLoading}
      />
      
      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      
      {/* Variant Upload Modal */}
      <FileUploadModalNew
        open={uploadModalOpen}
        onClose={handleCloseUploadModal}
        onUploadSuccess={handleUploadSuccess}
        app="product"
        table="variants"
        title="Upload"
        uploadFunction={(formData) => {
          // For variants upload, we need to specify the 'type' parameter
          formData.append('type', 'variants');
          return uploadProductsFile(formData);
        }}
        downloadTemplateFunction={(fmt) => downloadProductTemplate('variants', fmt)}
      />
      
      {/* Marketplace Listings Modal */}
      <MarketplaceListingsModal
        open={listingsModalOpen}
        onClose={handleCloseListingsModal}
        skuId={selectedSku?.id}
        skuCode={selectedSku?.sku}
      />
    </Box>
  );
};

export default VariantsPage;
